import type { OperatingSchedule } from '../../domain/types.js';
import { inWorldDateFromIso, inWorldWeekKeyFromDate } from './clock.js';
import type { PersistedRuntimeState } from './types.js';
import { RUNTIME_STATE_SCHEMA_VERSION } from './types.js';

const KEY_PREFIX = 'specimen-observer:runtime:';

/** Line-count chat logs and the old launch sentence. Location history no longer keeps these. */
const RETIRED_HISTORY = /talked \(\d+ lines\)|ready to observe|opened with \d+ character/;

export function isRetiredHistorySummary(summary: string): boolean {
  return RETIRED_HISTORY.test(summary);
}

const HISTORY_KEEP_MS = 7 * 24 * 60 * 60 * 1000;

function summaryKey(summary: string): string {
  return summary.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Drop line-count notes, duplicate sentences, and dates far from the world clock. */
export function withoutRetiredHistory(state: PersistedRuntimeState, nowIso?: string): PersistedRuntimeState {
  const source = state.historyEvents ?? [];
  const now = nowIso ? new Date(nowIso).getTime() : NaN;
  const seen = new Set<string>();
  const historyEvents = source.filter((event) => {
    const summary = event.summary ?? '';
    if (isRetiredHistorySummary(summary)) return false;
    const at = new Date(event.occurredAt).getTime();
    if (Number.isFinite(now) && Number.isFinite(at) && Math.abs(now - at) > HISTORY_KEEP_MS) return false;
    const key = summaryKey(summary);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (historyEvents.length === source.length) return state;
  const kept = new Set(historyEvents.map((event) => event.eventId));
  return {
    ...state,
    historyEvents,
    eventObservations: (state.eventObservations ?? []).filter((obs) => kept.has(obs.eventId)),
    summaries: (state.summaries ?? []).filter(
      (summary) => summary.eventIds.every((id) => kept.has(id))
        && !isRetiredHistorySummary(summary.observedFacts)
        && summary.eventIds.length > 0,
    ),
  };
}

export function runtimeStorageKey(worldId: string): string {
  return `${KEY_PREFIX}${worldId}`;
}

function migrateState(raw: PersistedRuntimeState, schedule: OperatingSchedule): PersistedRuntimeState {
  const inWorldDate = raw.currentInWorldDate || inWorldDateFromIso(raw.clock.inWorldIso, schedule);
  return {
    ...raw,
    schemaVersion: RUNTIME_STATE_SCHEMA_VERSION,
    deviceWeekKey: raw.deviceWeekKey ?? inWorldWeekKeyFromDate(inWorldDate),
    eventObservations: raw.eventObservations ?? [],
    usedDevicesThisWeek: raw.usedDevicesThisWeek ?? [],
    characterAbsences: raw.characterAbsences ?? [],
  };
}

export function loadRuntimeState(worldId: string, schedule?: OperatingSchedule): PersistedRuntimeState | null {
  try {
    const raw = localStorage.getItem(runtimeStorageKey(worldId));
    if (!raw) return null;
    const data = JSON.parse(raw) as PersistedRuntimeState;
    if (!data?.worldId || data.worldId !== worldId) return null;
    const migrated = schedule ? migrateState(data, schedule) : data;
    const cleaned = withoutRetiredHistory(migrated, migrated.clock?.inWorldIso);
    if (cleaned !== migrated) saveRuntimeState(cleaned);
    return cleaned;
  } catch {
    return null;
  }
}

export function saveRuntimeState(state: PersistedRuntimeState): void {
  try {
    localStorage.setItem(runtimeStorageKey(state.worldId), JSON.stringify(state));
  } catch {
    // Unwritable storage — session-only runtime.
  }
}

export function clearRuntimeState(worldId: string): void {
  try {
    localStorage.removeItem(runtimeStorageKey(worldId));
  } catch {
    // ignore
  }
}
